/**
 * EstimatingTabWithSubTabs - Wrapper component that contains Bid Sheets, Labor, Materials, Equipment as sub-tabs
 */
import { useState, useRef } from "react";
import { Users, Package, Wrench, FileSpreadsheet, Ruler } from "lucide-react";
import { Tabs, TabsContent } from "../ui/tabs";
import { UnderlineTabsList as TabsList, UnderlineTabsTrigger as TabsTrigger } from "../ui/tabs-variants";
import { useConfirm } from "../ui/confirm-dialog";
import apiClient from "../../services/api";
import EstimatingTab from "./EstimatingTab";
import TakeOffTab from "./TakeOffTab";
import LaborTab from "./LaborTab";
import MaterialsTab from "./MaterialsTab";
import EquipmentTab from "./EquipmentTab";

const EstimatingTabWithSubTabs = ({
  job,
  materials,
  equipment,
  suppliers,
  materialCategories,
  equipmentCategories,
  equipmentUnits,
  employeeRoles,
  miscItemsFromSettings,
  miscUnits,
  defaultBurdenPercent,
  defaultSalesTaxPercent,
  canManageJobs,
  canEditEstimating,
  userRole,
  bidSheets,
  onSaveBidSheet,
  onApproveCO,
  onRevokeCOApproval,
  onSaveLaborEstimates,
  onSaveMaterialEstimates,
  onSaveEquipmentEstimates,
  onPopulateFromBidSheet,
  onAddInvoice,
  onEditInvoice,
  onDeleteInvoice,
  onToggleInvoicePaid,
  onRefreshJob,
  getAuthHeaders,
}) => {
  const [activeSubTab, setActiveSubTab] = useState("bid-sheets");
  const takeoffHasChangesRef = useRef(false);
  const confirm = useConfirm();
  
  const handleTabChange = async (newTab) => {
    if (activeSubTab === "takeoff" && takeoffHasChangesRef.current) {
      const ok = await confirm({ title: "Unsaved Changes", message: "You have unsaved Take Off changes. Leave without saving?", confirmText: "Leave", confirmVariant: "destructive" });
      if (!ok) return;
    }
    setActiveSubTab(newTab);
  };
  
  const hasBidSheet = bidSheets?.some(
    bs => !bs.bid_type?.includes("change_order") && !bs.is_change_order && bs.grand_total > 0
  );

  return (
    <div className="space-y-4">
      <Tabs value={activeSubTab} onValueChange={handleTabChange}>
        <TabsList data-testid="estimating-subtabs">
          <TabsTrigger value="takeoff" data-testid="takeoff-subtab" className="text-sm">
            <Ruler className="w-4 h-4 mr-1.5" />
            Take Off
          </TabsTrigger>
          <TabsTrigger value="bid-sheets" data-testid="bid-sheets-subtab" className="text-sm">
            <FileSpreadsheet className="w-4 h-4 mr-1.5" />
            Bid Sheets
          </TabsTrigger>
          <TabsTrigger value="labor" data-testid="labor-subtab" className="text-sm">
            <Users className="w-4 h-4 mr-1.5" />
            Labor
          </TabsTrigger>
          <TabsTrigger value="materials" data-testid="materials-subtab" className="text-sm">
            <Package className="w-4 h-4 mr-1.5" />
            Material
          </TabsTrigger>
          <TabsTrigger value="equipment" data-testid="equipment-subtab" className="text-sm">
            <Wrench className="w-4 h-4 mr-1.5" />
            Equipment
          </TabsTrigger>
        </TabsList>

        <TabsContent value="takeoff" className="mt-4">
          <TakeOffTab
            job={job}
            materials={materials}
            onHasChangesChange={(val) => { takeoffHasChangesRef.current = val; }}
            onSaveTakeoff={async (takeoffData) => {
              // Save takeoff data via the job update endpoint
              await apiClient.put(`/jobs/${job.id}`, { takeoff: takeoffData });
              if (onRefreshJob) onRefreshJob();
            }}
          />
        </TabsContent>

        <TabsContent value="bid-sheets" className="mt-4">
          <EstimatingTab
            job={job}
            materials={materials}
            equipment={equipment}
            materialCategories={materialCategories}
            equipmentCategories={equipmentCategories}
            equipmentUnits={equipmentUnits}
            employeeRoles={employeeRoles}
            miscItemsFromSettings={miscItemsFromSettings}
            miscUnits={miscUnits}
            defaultBurdenPercent={defaultBurdenPercent}
            defaultSalesTaxPercent={defaultSalesTaxPercent}
            canManageJobs={canEditEstimating}
            userRole={userRole}
            onSaveBidSheet={onSaveBidSheet}
            onApproveCO={onApproveCO}
            onRevokeCOApproval={onRevokeCOApproval}
          />
        </TabsContent>

        <TabsContent value="labor" className="mt-4">
          <LaborTab
            job={job}
            canManageJobs={canManageJobs}
            onSaveLaborEstimates={onSaveLaborEstimates}
            onPopulateFromBidSheet={onPopulateFromBidSheet}
            hasBidSheet={hasBidSheet}
          />
        </TabsContent>

        <TabsContent value="materials" className="mt-4">
          <MaterialsTab
            job={job}
            suppliers={suppliers}
            canManageJobs={canManageJobs}
            onSaveMaterialEstimates={onSaveMaterialEstimates}
            onAddInvoice={onAddInvoice}
            onEditInvoice={onEditInvoice}
            onDeleteInvoice={onDeleteInvoice}
            onToggleInvoicePaid={onToggleInvoicePaid}
            onPopulateFromBidSheet={onPopulateFromBidSheet}
            hasBidSheet={hasBidSheet}
            onRefreshJob={onRefreshJob}
          />
        </TabsContent>

        <TabsContent value="equipment" className="mt-4">
          <EquipmentTab
            job={job}
            canManageJobs={canManageJobs}
            onSaveEquipmentEstimates={onSaveEquipmentEstimates}
            onPopulateFromBidSheet={onPopulateFromBidSheet}
            hasBidSheet={hasBidSheet}
            getAuthHeaders={getAuthHeaders}
            onJobUpdate={onRefreshJob}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default EstimatingTabWithSubTabs;
